import { keepPreviousData, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerListCommissionStatementsQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
} from 'korint-sdk';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ErrorRow } from '../../components/module-elements/ErrorRow';
import { LoadingRow } from '../../components/module-elements/LoadingRow';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { WarningBanner } from '../../components/forms/warning-banner/WarningBanner';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { AnnualDeclarationsCard } from './components/AnnualDeclarationsCard';
import { KpiCard } from './components/KpiCard';
import { MonthRow } from './components/MonthRow';
import { formatAmount } from './formatAmount';
import { formatMonth } from './formatMonth';
import { Text } from '@folio/components/ui/text';
import { Stack } from '@folio/components/ui/flex';

type Direction = 'paid' | 'received';

export function CommissionsModule() {
  const { t, i18n } = useTranslation();
  const [year, setYear] = useState<number | undefined>(undefined);

  const { data: user } = useSuspenseQuery({ ...legacyPermissionsControllerAtMeQueryOptions() });

  const { data, status, isPlaceholderData } = useQuery({
    ...brokerageFirmControllerListCommissionStatementsQueryOptions(user.brokerageFirmId!, {
      year,
    }),
    enabled: !!user.brokerageFirmId,
    placeholderData: keepPreviousData,
  });

  if (!user.brokerageFirmId) {
    return null;
  }

  const direction: Direction = data?.firmType === 'WHOLESALE' ? 'paid' : 'received';
  // The previous year stays in `data` while another loads: only its year list is still valid.
  const current = isPlaceholderData ? undefined : data;
  // Newest month first, as a statement reads.
  const months = current ? [...current.months].reverse() : [];
  const isRetail = data?.firmType === 'RETAIL';

  return (
    <Stack gap={6}>
      {current && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <KpiCard
            label={t(`commissions.kpis.total.${direction}`)}
            value={formatAmount(current.kpis.totalCents)}
            hint={t('commissions.kpis.year', { year: current.year })}
          />
          <KpiCard
            label={t('commissions.kpis.monthlyAverage')}
            value={formatAmount(current.kpis.monthlyAverageCents)}
            hint={t('commissions.kpis.closedMonths', { count: current.kpis.closedMonths })}
          />
          {current.kpis.commissionedFirms && (
            <KpiCard
              label={t('commissions.kpis.commissionedFirms')}
              value={`${current.kpis.commissionedFirms.commissionedCount}/${current.kpis.commissionedFirms.activeCount}`}
              hint={t('commissions.kpis.commissionedFirmsHint', {
                month: formatMonth(current.kpis.commissionedFirms.period, i18n.language),
              })}
            />
          )}
          {isRetail && current.kpis.outstandingCents !== null && (
            <KpiCard
              label={t('commissions.kpis.outstanding')}
              value={formatAmount(current.kpis.outstandingCents)}
              hint={t('commissions.kpis.outstandingHint')}
            />
          )}
        </div>
      )}

      {data && isRetail && !data.hasValidIban && (
        <WarningBanner
          title={t('commissions.ibanBanner.title')}
          message={t('commissions.ibanBanner.message')}
        />
      )}

      {/* Declarations span every year, so the previous year's list stays valid while one loads. */}
      {data && direction === 'received' && data.annualDeclarations.length > 0 && (
        <AnnualDeclarationsCard
          brokerageFirmId={user.brokerageFirmId}
          declarations={data.annualDeclarations}
        />
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-primary">{t('commissions.monthly.title')}</CardTitle>
          {data && data.years.length > 1 && (
            <Tabs value={String(year ?? data.year)} onValueChange={value => setYear(Number(value))}>
              <TabsList>
                {data.years.map(selectableYear => (
                  <TabsTrigger key={selectableYear} value={String(selectableYear)}>
                    {selectableYear}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('commissions.monthly.columns.month')}</TableHead>
                  {isRetail && (
                    <TableHead className="text-right">
                      {t('commissions.monthly.columns.amountDue')}
                    </TableHead>
                  )}
                  <TableHead className="text-right">
                    {data && t(`commissions.monthly.columns.amount.${direction}`)}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('commissions.monthly.columns.download')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {status === 'pending' || isPlaceholderData ? (
                  // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
                  Array.from({ length: 12 }).map((_, index) => <LoadingRow key={index} />)
                ) : status === 'error' ? (
                  <ErrorRow />
                ) : (
                  months.map(month => (
                    <MonthRow
                      key={month.period}
                      month={month}
                      showAmountDue={isRetail}
                      showTransferFiles={direction === 'paid'}
                    />
                  ))
                )}
              </TableBody>
              {current && (
                <TableFooter>
                  <TableRow>
                    <TableCell className="font-semibold">
                      {t('commissions.monthly.total', { year: current.year })}
                    </TableCell>
                    {/* Balances carry over from month to month: adding them up counts money twice. */}
                    {isRetail && <TableCell />}
                    <TableCell className="text-right font-semibold">
                      {formatAmount(current.kpis.totalCents)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
          {current && (
            <Text size="sm" tone="muted" className="mt-4">
              {t('commissions.kpis.closedMonths', { count: current.kpis.closedMonths })}
            </Text>
          )}
          {current && isRetail && (
            <Text size="sm" tone="muted" className="mt-2">
              {t('commissions.monthly.detailHint')}
            </Text>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
